import type { PointerInput } from "./runtime";

const JOYSTICK_DEAD_ZONE = 0.28;

/** Binds the touch-only movement stick and special-attack button. */
export function bindTouchControls(
  controls: HTMLElement,
  joystick: HTMLElement,
  stick: HTMLElement,
  special: HTMLButtonElement,
  input: PointerInput,
): void {
  const touchscreen =
    navigator.maxTouchPoints > 0 ||
    window.matchMedia("(any-pointer: coarse)").matches;
  if (!touchscreen) return;

  controls.hidden = false;
  let joystickPointer: number | null = null;
  let specialPointer: number | null = null;

  const moveJoystick = (event: PointerEvent): void => {
    if (event.pointerId !== joystickPointer) return;
    event.preventDefault();
    const bounds = joystick.getBoundingClientRect();
    const radius = Math.min(bounds.width, bounds.height) * 0.5;
    const dx = event.clientX - (bounds.left + bounds.width * 0.5);
    const dy = event.clientY - (bounds.top + bounds.height * 0.5);
    const distance = Math.hypot(dx, dy);
    const scale = distance > radius ? radius / distance : 1;
    const visualRadius = Math.max(
      0,
      radius - Math.min(stick.clientWidth, stick.clientHeight) * 0.5,
    );
    const visualScale = distance > 0 ? (visualRadius * scale) / radius : 0;
    stick.style.transform = `translate3d(${dx * visualScale}px, ${dy * visualScale}px, 0)`;

    const horizontal = dx / radius;
    const vertical = dy / radius;
    input.setHeld(0, vertical < -JOYSTICK_DEAD_ZONE);
    input.setHeld(1, vertical > JOYSTICK_DEAD_ZONE);
    input.setHeld(2, horizontal < -JOYSTICK_DEAD_ZONE);
    input.setHeld(3, horizontal > JOYSTICK_DEAD_ZONE);
  };

  const releaseJoystick = (event?: PointerEvent): void => {
    if (event && event.pointerId !== joystickPointer) return;
    joystickPointer = null;
    joystick.classList.remove("is-active");
    stick.style.transform = "translate3d(0, 0, 0)";
    for (let slot = 0; slot < 4; slot++) input.setHeld(slot, false);
  };

  joystick.addEventListener("pointerdown", (event) => {
    if (joystickPointer !== null) return;
    event.preventDefault();
    joystickPointer = event.pointerId;
    joystick.setPointerCapture(event.pointerId);
    joystick.classList.add("is-active");
    moveJoystick(event);
  });
  joystick.addEventListener("pointermove", moveJoystick);
  joystick.addEventListener("pointerup", releaseJoystick);
  joystick.addEventListener("pointercancel", releaseJoystick);
  joystick.addEventListener("lostpointercapture", releaseJoystick);

  const releaseSpecial = (event?: PointerEvent): void => {
    if (event && event.pointerId !== specialPointer) return;
    specialPointer = null;
    special.classList.remove("is-active");
    input.setHeld(4, false);
  };

  special.addEventListener("pointerdown", (event) => {
    if (specialPointer !== null) return;
    event.preventDefault();
    specialPointer = event.pointerId;
    special.setPointerCapture(event.pointerId);
    special.classList.add("is-active");
    input.setHeld(4, true);
    input.press(4);
  });
  special.addEventListener("pointerup", releaseSpecial);
  special.addEventListener("pointercancel", releaseSpecial);
  special.addEventListener("lostpointercapture", releaseSpecial);

  window.addEventListener("blur", () => {
    releaseJoystick();
    releaseSpecial();
  });
}
